// Журнал изменений: кто, что, когда, было и стало, фильтр по источнику, откат кнопкой.

import { useState } from 'react'

import { ApiError } from '@/api/client'
import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, Select, useToast } from '@/components/ui'
import { formatDateTime } from '@/lib/format'

import { useChanges, useUndoChange, type Change } from './api'
import { actorText, diffRows, OP_LABELS, SOURCE_OPTIONS } from './logic'

function ChangeRow({ change, onUndo, busy }: { change: Change; onUndo: () => void; busy: boolean }) {
  const [open, setOpen] = useState(false)
  const rows = open ? diffRows(change) : []
  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-fg">{change.summary_ru}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
            <span>{formatDateTime(change.at)}</span>
            <span>{actorText(change)}</span>
            <Badge tone={change.op === 'delete' ? 'warning' : 'neutral'}>{OP_LABELS[change.op]}</Badge>
            {change.undone_by ? <Badge>отменено</Badge> : null}
          </div>
        </div>
        {change.before || change.after ? (
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Скрыть' : 'Подробнее'}
          </Button>
        ) : null}
        {change.can_undo ? (
          <Button size="sm" onClick={onUndo} disabled={busy}>
            Откатить
          </Button>
        ) : null}
      </div>
      {open ? (
        <table className="mt-2 w-full text-xs">
          <thead className="text-left text-muted">
            <tr>
              <th className="py-1 pr-2 font-medium">Поле</th>
              <th className="py-1 pr-2 font-medium">Было</th>
              <th className="py-1 font-medium">Стало</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border align-top">
                <td className="py-1 pr-2 text-muted">{r.key}</td>
                <td className="break-all py-1 pr-2 tabular-nums">{r.before}</td>
                <td className="break-all py-1 tabular-nums">{r.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </li>
  )
}

export function ChangesPage() {
  const toast = useToast()
  const [source, setSource] = useState('')
  const list = useChanges(source)
  const undo = useUndoChange()
  const items = list.data?.pages.flatMap((p) => p.items) ?? []

  const rollback = (change: Change) =>
    undo.mutate(change.id, {
      onSuccess: () => toast.show({ message: 'Изменение отменено', tone: 'accent' }),
      onError: (error) =>
        toast.show({ message: error instanceof ApiError ? error.message : 'Не получилось отменить', tone: 'danger' }),
    })

  return (
    <>
      <PageHeader
        title="Журнал изменений"
        description="Любую запись можно откатить; откат тоже попадает в журнал"
        actions={
          <Select
            aria-label="Источник"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={SOURCE_OPTIONS}
          />
        }
      />
      <Card>
        {list.isLoading ? (
          <PageSpinner />
        ) : list.error ? (
          <ErrorText error={list.error} />
        ) : items.length === 0 ? (
          <EmptyState icon="history" title="Записей нет" description="Здесь появятся изменения данных." />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((c) => (
              <ChangeRow key={c.id} change={c} busy={undo.isPending} onUndo={() => rollback(c)} />
            ))}
          </ul>
        )}
        {list.hasNextPage ? (
          <div className="mt-3">
            <Button size="sm" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
              Показать ещё
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  )
}
